import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import {
  createSupportGroup,
  deleteSupportGroup,
  fetchSupportGroup,
  fetchSupportGroups,
  fetchSupportGroupsAvailableForAssignment,
  updateSupportGroup,
} from '../api/supportGroups';
import { fetchUserId } from '../api/fetchUser';
import { getUserProfile } from '../configuration/login/loginSlice';
import { SupportGroup, SupportGroupFilters, SupportGroupFormData } from '../models/supportGroup';
import { useDebounce } from './useDebounce';

export const useSupportGroups = () => {
  const userProfile = useSelector(getUserProfile);
  const intl = useIntl();

  const [groups, setGroups] = useState<SupportGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userHasAccess, setUserHasAccess] = useState<boolean | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [level, setLevel] = useState<SupportGroupFilters['level']>();
  const [active, setActive] = useState<boolean>();
  const debouncedSearchTerm = useDebounce(searchTerm, 400);

  const [editingGroup, setEditingGroup] = useState<SupportGroup | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState<SupportGroup | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const getErrorMessage = (err: any, fallbackId: string) => {
    if (err?.response?.status === 403) return intl.formatMessage({ id: 'intl-msg:backoffice.supportGroups.forbidden' });
    if (err?.response?.status === 400) return intl.formatMessage({ id: 'intl-msg:backoffice.supportGroups.invalid' });
    return intl.formatMessage({ id: fallbackId });
  };

  const loadGroups = useCallback(async () => {
    setError(null);
    try {
      const userId = await fetchUserId(userProfile);
      const authorizedUserIds = import.meta.env.VITE_BACKOFFICE_AUTHORIZED_USER_IDS?.split(',').map((id: string) => id.trim()) || [];
      if (!userId || !authorizedUserIds.includes(userId)) {
        setUserHasAccess(false);
        return;
      }
      setUserHasAccess(true);
      setGroups(await fetchSupportGroups({ searchTerm: debouncedSearchTerm || undefined, level, active }));
    } catch (err) {
      setError(intl.formatMessage({ id: 'intl-msg:backoffice.supportGroups.loadError' }));
      console.error('Error loading support groups:', err);
    } finally {
      setLoading(false);
    }
  }, [userProfile, debouncedSearchTerm, level, active]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  const openNewForm = () => {
    setEditingGroup(null);
    setShowForm(true);
  };

  const openEditForm = async (group: SupportGroup) => {
    try {
      setEditingGroup(await fetchSupportGroup(group.id));
      setShowForm(true);
    } catch (err) {
      setError(getErrorMessage(err, 'intl-msg:backoffice.supportGroups.loadError'));
    }
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingGroup(null);
  };

  const saveGroup = async (data: SupportGroupFormData): Promise<string | null> => {
    try {
      if (editingGroup) {
        await updateSupportGroup(editingGroup.id, data);
      } else {
        await createSupportGroup(data);
      }
      closeForm();
      await loadGroups();
      return null;
    } catch (err) {
      return getErrorMessage(err, 'intl-msg:backoffice.supportGroups.saveError');
    }
  };

  const confirmDelete = async () => {
    if (!groupToDelete) return;
    setDeleteLoading(true);
    try {
      await deleteSupportGroup(groupToDelete.id);
      setGroupToDelete(null);
      await loadGroups();
    } catch (err) {
      setGroupToDelete(null);
      setError(getErrorMessage(err, 'intl-msg:backoffice.supportGroups.deleteError'));
    } finally {
      setDeleteLoading(false);
    }
  };

  return {
    groups,
    loading,
    error,
    userHasAccess,
    searchTerm,
    setSearchTerm,
    level,
    setLevel,
    active,
    setActive,
    editingGroup,
    showForm,
    openNewForm,
    openEditForm,
    closeForm,
    saveGroup,
    groupToDelete,
    setGroupToDelete,
    confirmDelete,
    deleteLoading,
  };
};

export const useSupportGroupOptions = () => {
  const [groups, setGroups] = useState<SupportGroup[]>([]);

  useEffect(() => {
    fetchSupportGroupsAvailableForAssignment().then(setGroups);
  }, []);

  return groups.map(group => ({ id: String(group.id), label: group.name }));
};
